/**
 * Slider — editor component.
 *
 * Static row preview (no Embla in the editor): slides lay out in a flex row
 * sized by the same --embla-slides-* and --embla-gap custom properties the
 * frontend uses, so the editor width math matches embla.min.css. Off-canvas
 * slides peek via horizontal scroll (editor.scss).
 */
import { __, sprintf } from '@wordpress/i18n';
import {
  useBlockProps,
  useInnerBlocksProps,
  InspectorControls,
  BlockControls,
  store as blockEditorStore,
} from '@wordpress/block-editor';
import { PanelBody, ToolbarButton, ToolbarGroup } from '@wordpress/components';
import { useSelect } from '@wordpress/data';
import { useCallback, useEffect, useRef, useState } from '@wordpress/element';

import LayoutPanel from './InspectorControls/LayoutPanel';
import BehaviorPanel from './InspectorControls/BehaviorPanel';
import SliderControlsPanel from '../../../utils/slider-controls-panel';
import { controlColorVars } from '../../../utils/slider-control-colors';
import { getSliderGapValue } from '../../../utils/helpers';
import SliderControlsSkeleton, {
  navHostClass,
  previewDotCount,
} from '../../../utils/slider-controls-skeleton';
import { SLIDER_STARTER_SLIDES } from '../../_shared/slider-slide-defaults';
import useStepPlayback from '../../_shared/slider-preview-playback';
import '../editor.scss';

/**
 * Clamp a slides-per-view value for the canvas preview.
 *
 * Fractions are meaningful here — the width formula divides by this — so it
 * parses as a float and mirrors render.php's [1, 6] clamp rather than rounding
 * a deliberate 1.15 down to a full-width slide.
 *
 * @param {*}      value    Saved attribute value.
 * @param {number} fallback Value to use when the attribute is missing or junk.
 * @return {number} Clamped per-view count.
 */
function clampSlidesPerView(value, fallback) {
  const number = parseFloat(value);

  return Math.min(6, Math.max(1, Number.isFinite(number) ? number : fallback));
}

/**
 * One "step" is a single slide, matching Embla's align:start behaviour on the
 * frontend rather than paging by a whole viewport.
 *
 * @param {HTMLElement|null} strip         The scrolling slides container.
 * @param {number}           slidesPerView Slides visible at once.
 * @return {number} Pixel distance of one step.
 */
const stepWidth = (strip, slidesPerView) =>
  strip ? strip.clientWidth / Math.max(1, slidesPerView) : 0;

export default function Edit({ attributes, setAttributes, clientId }) {
  const {
    slidesPerView = {},
    gap,
    controlsPlacement = '',
    controlsDots = '',
    controlsNav = '',
    showArrows = true,
    showDots = true,
    loop = true,
    autoplay = false,
    autoplayMode = 'step',
    autoplayDelay = 4000,
    autoScrollSpeed = 2,
  } = attributes;

  const [playOverride, setPlayOverride] = useState(null);

  // Inherit ("") cannot be resolved client-side — theme.json and the
  // brandy_slider_controls filter both run server-side — so the canvas shows the
  // built-in default until the editor picks an explicit placement.
  const placement = controlsPlacement || 'default';

  /*
   * `controlsReveal` is deliberately NOT stamped onto the canvas. The skeleton
   * exists so a controls choice is visible while configuring it, and a
   * hover-reveal preview would hide the very thing the inspector is pointing
   * at. The panel's help text carries the frontend behaviour instead.
   */

  const { slideCount, isBeingEdited, isPreviewMode } = useSelect(
    (select) => {
      const store = select(blockEditorStore);

      return {
        slideCount: store.getBlockCount(clientId),
        /*
         * Selecting the block — or anything inside it — is the signal that the
         * author came to edit rather than to look, and a strip that scrolls
         * itself out from under a caret is unusable.
         */
        isBeingEdited:
          store.isBlockSelected(clientId) ||
          store.hasSelectedInnerBlock(clientId, true),
        /*
         * True inside a BlockPreview — the pattern inserter, and the block
         * inserter's own hover preview. Nobody is editing there; the render is
         * a catalogue entry being browsed.
         */
        isPreviewMode: store.getSettings().isPreviewMode,
      };
    },
    [clientId]
  );

  /*
   * Playing the preview is what makes a delay judgeable without publishing —
   * Embla never runs in the canvas, so an autoplaying slider otherwise sits
   * perfectly still while it is being configured. The strip already scrolls
   * (editor.scss), so stepping it is a scrollTo rather than a second layout.
   */
  const stripRef = useRef(null);
  const perView = clampSlidesPerView(slidesPerView.desktop, 3);
  const isDrift = autoplayMode === 'scroll';
  const steps = Math.max(1, slideCount - Math.floor(perView) + 1);

  /*
   * Null until the author presses the toolbar button, which then pins their
   * choice — the same override the Scrolling (marquee) block keeps. Left alone,
   * an idle slider runs only if `autoplay` is on: on the canvas the preview is
   * meant to show what a visitor will see, and a slider that waits for a click
   * on the frontend should wait here too rather than advertise motion it does
   * not have.
   *
   * A preview is the exception, because it answers a different question. On the
   * canvas the author already knows this is a slider and is judging one
   * configured instance; in the inserter they are deciding whether this pattern
   * is the one they want, and a strip parked on slide one is indistinguishable
   * from a plain row of columns — the slides behind it, which are most of what
   * the pattern is, never get seen. Showcase Slider already takes this position
   * (its preview runs off `showPreview`, with no autoplay gate); this brings the
   * two into line. No toolbar and no selection exist inside a preview, so the
   * other two terms are dead there and the override still wins on the canvas.
   */
  const isPlaying =
    (playOverride ?? (isPreviewMode || (autoplay && !isBeingEdited))) &&
    steps > 1;

  const releasePlay = useCallback(() => setPlayOverride(false), []);

  const { index, goTo } = useStepPlayback({
    count: steps,
    delay: autoplayDelay,
    playing: isPlaying,
    loop,
    stepping: !isDrift,
    onEnd: releasePlay,
  });

  // Step mode: the index is the position, so applying it is a scroll.
  useEffect(() => {
    const strip = stripRef.current;

    if (!strip || isDrift) {
      return;
    }

    strip.scrollTo({ left: index * stepWidth(strip, perView), behavior: 'smooth' });
  }, [index, perView, isDrift]);

  /*
   * Auto-scroll mode drifts rather than steps, so it drives the strip directly.
   * `autoScrollSpeed` is Embla's own px-per-frame figure, which is why this
   * advances by it per frame instead of converting to a duration.
   */
  useEffect(() => {
    if (!isPlaying || !isDrift) {
      return undefined;
    }

    let frame = 0;

    const drift = () => {
      const strip = stripRef.current;

      if (strip) {
        const limit = strip.scrollWidth - strip.clientWidth;

        if (strip.scrollLeft >= limit - 1) {
          // Snapping back is the honest preview of a looping auto-scroll: the
          // frontend's seamless wrap needs Embla's cloned slides, which the
          // canvas has no equivalent for.
          if (!loop) {
            releasePlay();
            return;
          }

          strip.scrollLeft = 0;
        } else {
          strip.scrollLeft += Math.max(0.2, autoScrollSpeed);
        }
      }

      frame = window.requestAnimationFrame(drift);
    };

    frame = window.requestAnimationFrame(drift);

    return () => window.cancelAnimationFrame(frame);
  }, [isPlaying, isDrift, autoScrollSpeed, loop, releasePlay]);

  const handleToggle = useCallback(() => {
    if (!isPlaying) {
      // Starting from the first slide; picking up wherever a previous run
      // stopped looks like the block lost its place.
      goTo(0);
    }

    setPlayOverride(!isPlaying);
  }, [isPlaying, goTo]);

  const blockProps = useBlockProps({
    className: ['brandy-slider is-editor-preview', navHostClass(controlsNav)]
      .filter(Boolean)
      .join(' '),
    'data-controls-placement': placement,
    style: {
      '--embla-slides-desktop': perView,
      '--embla-slides-tablet': clampSlidesPerView(slidesPerView.tablet, 2),
      '--embla-slides-mobile': clampSlidesPerView(slidesPerView.mobile, 1),
      // Normalised rather than passed through: UnitControl stores a bare `0`
      // for a cleared field, and one unitless number inside the width calc()
      // invalidates it, dropping the slide sizing altogether.
      '--embla-gap': getSliderGapValue(gap),
      // This element carries `is-editor-preview`, which is where embla.min.css
      // seeds the button colours — an element's own declaration beats an
      // inherited one, so the override has to be here to land.
      ...controlColorVars(attributes),
    },
  });

  const innerBlocksProps = useInnerBlocksProps(
    { className: 'brandy-slider__slides', ref: stripRef },
    {
      allowedBlocks: ['brandy/slide'],
      template: SLIDER_STARTER_SLIDES,
      orientation: 'horizontal',
    }
  );

  return (
    <>
      {/*
       * Toolbar placement follows the Scrolling (marquee) block: previewing
       * motion is a block-level mode, not a setting, so it belongs next to the
       * block's other mode switches rather than in the inspector.
       */}
      <BlockControls>
        <ToolbarGroup>
          <ToolbarButton
            icon={isPlaying ? 'controls-pause' : 'controls-play'}
            label={
              isPlaying
                ? __('Pause preview', 'brandy-blocks')
                : sprintf(
                    /* translators: 1: current slide number, 2: total slides. */
                    __('Play preview (slide %1$d of %2$d)', 'brandy-blocks'),
                    index + 1,
                    steps
                  )
            }
            onClick={handleToggle}
            isPressed={isPlaying}
            // Every slide already fits: there is nowhere to step to.
            disabled={steps < 2}
          />
        </ToolbarGroup>
      </BlockControls>
      <InspectorControls>
        <LayoutPanel attributes={attributes} setAttributes={setAttributes} />
        <BehaviorPanel attributes={attributes} setAttributes={setAttributes} />
        <PanelBody title={__('Slider controls', 'brandy-blocks')} initialOpen={false}>
          <SliderControlsPanel attributes={attributes} setAttributes={setAttributes} />
        </PanelBody>
      </InspectorControls>
      <div {...blockProps}>
        <div {...innerBlocksProps} />
        <SliderControlsSkeleton
          placement={placement}
          dotsStyle={controlsDots || 'default'}
          showArrows={showArrows}
          showDots={showDots}
          dotCount={previewDotCount(slideCount, slidesPerView.desktop)}
        />
      </div>
    </>
  );
}
